/**
 * Deterministic formatting for agent chat replies. The agent's prompt contract
 * (dearbill-agent, CHAT FORMATTING section) is plain text with dash bullets
 * and an optional machine-readable final line `[[options: A | B | C]]` naming
 * 2 to 4 closed choices. This module strips that line into quick replies and
 * splits the remaining text into paragraph and list blocks. It is not a
 * markdown parser; any line that doesn't match renders as plain text.
 */

/** A reply split into its visible text and the stripped quick-reply options. */
export interface ParsedAgentReply {
  text: string;
  quickReplies: string[];
}

/** Quick replies render as tap buttons only for a small closed set. */
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;

const OPTIONS_LINE = /^\[\[options:(.+)\]\]$/;

/**
 * Strips a trailing `[[options: A | B]]` line into `quickReplies`. A line that
 * is malformed, not last, or carries fewer than {@link MIN_OPTIONS} or more
 * than {@link MAX_OPTIONS} choices stays in the text untouched, so a
 * misbehaving model never hides content from the member.
 */
export function parseAgentReply(reply: string): ParsedAgentReply {
  const lines = reply.trimEnd().split("\n");
  const inner = OPTIONS_LINE.exec(lines[lines.length - 1]?.trim() ?? "")?.[1];
  if (inner !== undefined) {
    const options = inner.split("|").map((option) => option.trim());
    if (
      options.length >= MIN_OPTIONS &&
      options.length <= MAX_OPTIONS &&
      options.every((option) => option.length > 0)
    ) {
      return { text: lines.slice(0, -1).join("\n").trimEnd(), quickReplies: options };
    }
  }
  return { text: reply, quickReplies: [] };
}

/** One renderable chunk of a bubble: a paragraph line or a run of dash bullets. */
export type MessageBlock = { kind: "paragraph"; text: string } | { kind: "list"; items: string[] };

/**
 * Splits bubble text on newlines: consecutive `- ` lines group into one list
 * block, every other non-blank line is its own paragraph, and text content
 * (dollar amounts included) passes through verbatim. Text with no non-blank
 * lines falls back to a single paragraph of the original string.
 */
export function toMessageBlocks(text: string): MessageBlock[] {
  const blocks: MessageBlock[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const previous = blocks[blocks.length - 1];
    if (line.startsWith("- ")) {
      const item = line.slice(2).trim();
      if (previous?.kind === "list") previous.items.push(item);
      else blocks.push({ kind: "list", items: [item] });
    } else {
      blocks.push({ kind: "paragraph", text: line });
    }
  }
  if (blocks.length === 0) blocks.push({ kind: "paragraph", text });
  return blocks;
}
